import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHullStabilityFilterOptional } from '../contexts/HullStabilityFilterContext';
import { isStrokeIndexAllowed } from '../utils/hullStability/strokeAlignment';
import { useChartPropsPattern } from '../utils/chartPropsPattern';
import {
  fetchRawStrokeData,
  fetchPhysicsBasedPowerTimeSeriesWithMeta,
  fetchGateForceStrokeSamplerData,
  fetchGateForceMedianProfileSamplerData,
  isGateForceSamplerEnabled,
} from '../utils/chartDataFetchers';
import { applyPowerCorrections } from '../utils/parameterCorrections';
import type {
  GateForceProfileResult,
  GateForceStrokeSamplerResult,
} from '../services/telemetry/telemetryService';

// ===== TYPES =====
export interface GateForceData {
  angle: number;
  force: number;
  timestamp?: number;
  phase?: string;
  strokeNumber?: number;
}

export type ViewMode =
  | 'single-rower-all-strokes'
  | 'rower-profile'
  | 'all-rowers-one-stroke'
  | 'crew-profiles';

export interface XYPoint {
  x: number;
  y: number;
}

export type TracePoint = { x: number; y: number };
export type TraceSeries = TracePoint[];

export type GateForceFetcherResult =
  | {
      mode: 'sampler';
      strokeSamples: GateForceStrokeSamplerResult;
      medianProfiles: GateForceProfileResult | null;
    }
  | {
      mode: 'fallback';
      raw: Record<string, GateForceData[]> | null;
    };

export interface GateForceDataHookResult {
  gateForceData: GateForceFetcherResult | null;
  loading: boolean;
  error: string | null;
  corrections: any;
  correctionDisplay: any;
  retry: () => void;
  rowerNames: string[];
  strokesByRower: Record<string, TraceSeries[]>;
  profilesByRower: Record<string, XYPoint[]>;
  skippedStrokesByRower: Record<string, number>;
}

// ===== CONSTANTS =====
const ANGLE_GRID_POINTS = 180;
const MIN_STROKE_SAMPLES = 20;
const TARGET_PROFILE_POINTS = ANGLE_GRID_POINTS;
const TARGET_TRACE_POINTS = 180;
const EMPTY_STROKE_BOUNDARIES: any[] = [];

// ===== HELPER FUNCTIONS =====
const toTraceXYFromPoints = (stroke: GateForceData[]): TraceSeries => {
  if (!stroke?.length) return [];
  const series: TraceSeries = [];
  for (let i = 0; i < stroke.length; i += 1) {
    const point = stroke[i];
    if (Number.isFinite(point?.angle) && Number.isFinite(point?.force)) {
      series.push({ x: point.angle, y: point.force });
    }
  }
  return series;
};

const toTraceXYFromArrays = (
  angles: Float32Array,
  forces: Float32Array,
  correctionFactor: number
): TraceSeries => {
  const length = Math.min(angles?.length ?? 0, forces?.length ?? 0);
  const series: TraceSeries = [];
  for (let i = 0; i < length; i += 1) {
    const angle = angles[i];
    const force = forces[i];
    if (Number.isFinite(angle) && Number.isFinite(force)) {
      series.push({ x: angle, y: force * correctionFactor });
    }
  }
  return series;
};

// ===== ADDITIONAL HELPER FUNCTIONS =====
const detectStrokeCycles = (rawData: GateForceData[]): GateForceData[][] => {
  if (!rawData.length) return [];

  const strokes: GateForceData[][] = [];
  let currentStroke: GateForceData[] = [];
  let previousAngle = rawData[0]?.angle || 0;
  let isIncreasing = false;
  let strokeStarted = false;

  rawData.forEach((point, index) => {
    if (
      point.angle < -50 &&
      !isIncreasing &&
      index > 0 &&
      rawData[index + 1] &&
      rawData[index + 1].angle > point.angle
    ) {
      if (strokeStarted && currentStroke.length >= MIN_STROKE_SAMPLES) {
        strokes.push([...currentStroke]);
      }
      currentStroke = [point];
      strokeStarted = true;
      isIncreasing = true;
    } else if (strokeStarted) {
      currentStroke.push(point);
      isIncreasing = point.angle > previousAngle;
    }

    previousAngle = point.angle;
  });

  if (strokeStarted && currentStroke.length >= MIN_STROKE_SAMPLES) {
    strokes.push([...currentStroke]);
  }

  return strokes;
};

const smoothProfilePoints = (points: XYPoint[], windowSize = 5): XYPoint[] => {
  if (points.length === 0 || windowSize <= 1) return points;
  const size = windowSize % 2 === 0 ? windowSize + 1 : windowSize;
  const half = Math.floor(size / 2);
  return points.map((point, index) => {
    let sum = 0;
    let count = 0;
    for (let i = Math.max(0, index - half); i <= Math.min(points.length - 1, index + half); i++) {
      sum += points[i].y;
      count += 1;
    }
    if (count > 0) {
      const smoothed = sum / count;
      const edgeFactor = index < half || index > points.length - 1 - half ? 0.5 : 1;
      return {
        ...point,
        y: edgeFactor < 1 ? edgeFactor * smoothed + (1 - edgeFactor) * point.y : smoothed,
      };
    }
    return point;
  });
};

const median = (values: number[]): number => {
  if (!values.length) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const resampleTraceSeries = (
  stroke: TraceSeries,
  targetPoints: number = TARGET_PROFILE_POINTS
): { angle: number[]; force: number[] } => {
  if (!stroke.length) {
    return { angle: [], force: [] };
  }
  if (stroke.length === 1) {
    return {
      angle: Array(targetPoints).fill(stroke[0].x),
      force: Array(targetPoints).fill(stroke[0].y),
    };
  }

  const lastIndex = stroke.length - 1;
  const angle: number[] = new Array(targetPoints);
  const force: number[] = new Array(targetPoints);

  for (let i = 0; i < targetPoints; i += 1) {
    const t = targetPoints === 1 ? 0 : (i * lastIndex) / (targetPoints - 1);
    const idx = Math.floor(t);
    const frac = t - idx;
    const p0 = stroke[idx];
    const p1 = stroke[Math.min(idx + 1, lastIndex)];
    angle[i] = p0.x + (p1.x - p0.x) * frac;
    force[i] = p0.y + (p1.y - p0.y) * frac;
  }

  return { angle, force };
};

const decimateTraceSeries = (
  stroke: TraceSeries,
  targetPoints: number = TARGET_TRACE_POINTS
): TraceSeries => {
  if (stroke.length <= targetPoints) return stroke;
  const { angle, force } = resampleTraceSeries(stroke, targetPoints);
  const series: TraceSeries = [];
  const length = Math.min(angle.length, force.length);
  for (let i = 0; i < length; i += 1) {
    const x = angle[i];
    const y = force[i];
    if (Number.isFinite(x) && Number.isFinite(y)) {
      series.push({ x, y });
    }
  }
  return series;
};

const buildMedianProfileFromResampled = (
  angleArrays: number[][],
  forceArrays: number[][],
  windowSize = 5
): XYPoint[] => {
  if (!angleArrays.length || !forceArrays.length) return [];
  const length = Math.min(
    ...angleArrays.map((arr) => arr.length).filter((len) => len > 0),
    ...forceArrays.map((arr) => arr.length).filter((len) => len > 0)
  );

  const profile: XYPoint[] = [];
  for (let i = 0; i < length; i += 1) {
    const angleSamples: number[] = [];
    const forceSamples: number[] = [];

    angleArrays.forEach((arr) => {
      const value = arr[i];
      if (Number.isFinite(value)) angleSamples.push(value);
    });
    forceArrays.forEach((arr) => {
      const value = arr[i];
      if (Number.isFinite(value)) forceSamples.push(value);
    });

    if (!angleSamples.length || !forceSamples.length) continue;

    const angleMedian = median(angleSamples);
    const forceMedian = median(forceSamples);
    profile.push({ x: angleMedian, y: forceMedian });
  }

  return smoothProfilePoints(profile, windowSize);
};

// ===== DATA FETCHING FUNCTIONS =====
async function fetchFallbackGateForceData(
  sessionId: string,
  rowerPositions?: number[]
): Promise<Record<string, GateForceData[]> | null> {
  const allRowerData: Record<string, GateForceData[]> = {};

  // If rowerPositions specified, only fetch those positions (for rower compliance)
  // Otherwise, fetch all 8 positions (for coach view)
  const positionsToFetch =
    rowerPositions && rowerPositions.length > 0 ? rowerPositions : [1, 2, 3, 4, 5, 6, 7, 8];

  for (const rowerPosition of positionsToFetch) {
    try {
      const data = await fetchRawStrokeData(sessionId, rowerPosition);
      if (data && data.length > 0) {
        allRowerData[`Rower ${rowerPosition}`] = data;
      }
    } catch (error) {
      // Silently handle error - no console logging in hook
    }
  }

  if (Object.keys(allRowerData).length === 0) {
    return null;
  }

  return allRowerData;
}

async function fetchGateForceData(
  sessionId: string,
  rowerPositions?: number[]
): Promise<GateForceFetcherResult | null> {
  const samplerEnabled = isGateForceSamplerEnabled();

  if (samplerEnabled) {
    try {
      // Pass rowerPositions to filter server-side (compliance for rowers)
      const strokeSamples = await fetchGateForceStrokeSamplerData(sessionId, {
        rowerPositions,
      });

      if (strokeSamples && Object.keys(strokeSamples.series).length > 0) {
        let medianProfiles: GateForceProfileResult | null = null;
        try {
          // Pass rowerPositions to filter server-side (compliance for rowers)
          medianProfiles = await fetchGateForceMedianProfileSamplerData(sessionId, {
            rowerPositions,
            targetPoints: strokeSamples.meta.targetPoints,
          });
        } catch (profileError) {
          // Silently handle profile error
        }

        // If we have profile data for more rowers than stroke data,
        // create empty stroke entries for missing rowers
        if (
          medianProfiles &&
          Object.keys(medianProfiles.series).length > Object.keys(strokeSamples.series).length
        ) {
          const allRowerKeys = Object.keys(medianProfiles.series);
          const missingRowers = allRowerKeys.filter((key) => !strokeSamples.series[key]);

          missingRowers.forEach((rowerKey) => {
            strokeSamples.series[rowerKey] = {
              strokes: [],
              skippedStrokes: 0,
              totalStrokes: 0,
            };
          });
        }

        return {
          mode: 'sampler',
          strokeSamples,
          medianProfiles,
        };
      }
    } catch (samplerError) {
      // Silently handle sampler error and fall back to raw data
    }
  }

  const fallbackRaw = await fetchFallbackGateForceData(sessionId, rowerPositions);
  if (!fallbackRaw) {
    return null;
  }

  return {
    mode: 'fallback',
    raw: fallbackRaw,
  };
}

// ===== MAIN HOOK =====
export const useGateForceData = (
  sessionId: string,
  parameterInfo: any,
  rowerContext?: import('../types/rowerContext').RowerContext | null
): GateForceDataHookResult => {
  // Determine rowerPositions based on rowerContext
  // If rowerContext.mode === 'filter', only fetch that rower's data (compliance)
  const rowerPositions = useMemo(() => {
    if (rowerContext && rowerContext.mode === 'filter') {
      return [rowerContext.position];
    }
    return undefined; // Coach view: fetch all rowers
  }, [rowerContext]);

  const hullFilter = useHullStabilityFilterOptional();
  const allowedStrokeIndices = hullFilter?.allowedStrokeIndices ?? null;

  // Fetch data using existing pattern
  const {
    data: gateForceData,
    loading,
    error,
    corrections,
    correctionDisplay,
    retry,
  } = useChartPropsPattern<GateForceFetcherResult | null>(
    sessionId,
    parameterInfo,
    (sessionId: string) => fetchGateForceData(sessionId, rowerPositions),
    {
      queryKey: ['gate-force-signature-chart', sessionId, rowerPositions?.join(',')],
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Extract rower names
  const rowerNames = useMemo(() => {
    if (!gateForceData) return [] as string[];
    if (gateForceData.mode === 'sampler') {
      return Object.keys(gateForceData.strokeSamples.series);
    }
    if (gateForceData.raw) {
      return Object.keys(gateForceData.raw);
    }
    return [] as string[];
  }, [gateForceData]);

  // Fetch stroke boundaries
  // Only needed for fallback mode - sampler mode already has strokes pre-processed
  const { data: strokeBoundaries = EMPTY_STROKE_BOUNDARIES } = useQuery({
    queryKey: ['gate-force-stroke-boundaries', sessionId],
    enabled: !!sessionId && gateForceData?.mode === 'fallback',
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const meta = await fetchPhysicsBasedPowerTimeSeriesWithMeta(sessionId);
      return meta.strokeBoundaries || [];
    },
  });

  // Process data based on mode
  const processedRowerData = useMemo(() => {
    const empty = {
      strokes: {} as Record<string, TraceSeries[]>,
      profiles: {} as Record<string, XYPoint[]>,
      skipped: {} as Record<string, number>,
    };

    if (!gateForceData) {
      return empty;
    }

    const correctionFactor = corrections.correction ?? 1;

    if (gateForceData.mode === 'sampler') {
      const strokes: Record<string, TraceSeries[]> = {};
      const profiles: Record<string, XYPoint[]> = {};
      const skipped: Record<string, number> = {};

      // First, use server-side medianProfiles if available (from RPC)
      const serverProfiles = gateForceData.medianProfiles?.series || {};

      Object.entries(gateForceData.strokeSamples.series).forEach(([rowerName, samplerRow]) => {
        if (!samplerRow || samplerRow.strokes.length === 0) {
          strokes[rowerName] = [];
          profiles[rowerName] = [];
          skipped[rowerName] = samplerRow?.skippedStrokes ?? 0;
          return;
        }

        const resampledAngles: number[][] = [];
        const resampledForces: number[][] = [];

        const rowerStrokes = samplerRow.strokes
          .filter((strokeSeries) =>
            isStrokeIndexAllowed(strokeSeries.strokeIndex, allowedStrokeIndices)
          )
          .map((strokeSeries) => {
            const trace = toTraceXYFromArrays(
              strokeSeries.angle,
              strokeSeries.force,
              correctionFactor
            );
            if (trace.length) {
              const angles = Array.from(strokeSeries.angle, (value) => value);
              const forces = Array.from(strokeSeries.force, (value) => value * correctionFactor);
              resampledAngles.push(angles);
              resampledForces.push(forces);
            }
            return decimateTraceSeries(trace);
          })
          .filter((stroke) => stroke.length > 0);

        strokes[rowerName] = rowerStrokes;
        skipped[rowerName] = samplerRow.skippedStrokes ?? 0;

        const useClientProfile = allowedStrokeIndices != null;

        // When hull-set filter is active, always rebuild median from filtered strokes.
        if (!useClientProfile && serverProfiles[rowerName]) {
          const serverProfile = serverProfiles[rowerName];
          const profilePoints: XYPoint[] = [];
          const angleArray = serverProfile.angle;
          const forceArray = serverProfile.force;
          const length = Math.min(angleArray.length, forceArray.length);
          for (let i = 0; i < length; i++) {
            const angle = angleArray[i];
            const force = forceArray[i] * correctionFactor;
            if (Number.isFinite(angle) && Number.isFinite(force)) {
              profilePoints.push({ x: angle, y: force });
            }
          }
          profiles[rowerName] = profilePoints;
        } else {
          // Build profile from (possibly filtered) strokes client-side
          let anglesForProfile = resampledAngles;
          let forcesForProfile = resampledForces;

          if (!useClientProfile) {
            const completeStrokeIndices: number[] = [];
            resampledAngles.forEach((angles, idx) => {
              const maxAngle = Math.max(...angles);
              if (maxAngle > 30) {
                completeStrokeIndices.push(idx);
              }
            });
            anglesForProfile = completeStrokeIndices.map((idx) => resampledAngles[idx]);
            forcesForProfile = completeStrokeIndices.map((idx) => resampledForces[idx]);
          }

          if (anglesForProfile.length && forcesForProfile.length) {
            profiles[rowerName] = buildMedianProfileFromResampled(
              anglesForProfile,
              forcesForProfile
            );
          } else {
            profiles[rowerName] = [];
          }
        }
      });

      return { strokes, profiles, skipped };
    }

    const raw = gateForceData.raw;
    if (!raw) {
      return empty;
    }

    const corrected = Object.fromEntries(
      Object.entries(raw).map(([rowerKey, rowerData]) => [
        rowerKey,
        rowerData.map((point) => ({
          ...point,
          force: applyPowerCorrections([point.force], corrections.correction)[0],
        })),
      ])
    );

    const strokes: Record<string, TraceSeries[]> = {};
    const profiles: Record<string, XYPoint[]> = {};
    const skipped: Record<string, number> = {};

    Object.entries(corrected).forEach(([rowerName, data]) => {
      if (!Array.isArray(data) || data.length === 0) {
        strokes[rowerName] = [];
        profiles[rowerName] = [];
        skipped[rowerName] = 0;
        return;
      }

      const sorted = [...data]
        .filter((point) => typeof point.timestamp === 'number')
        .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

      const rowerStrokes: TraceSeries[] = [];
      const resampledAngles: number[][] = [];
      const resampledForces: number[][] = [];
      let pointer = 0;
      let skippedCount = 0;

      if (strokeBoundaries.length) {
        strokeBoundaries.forEach((boundary, boundaryIndex) => {
          const strokeIndex = boundaryIndex + 1;
          if (!isStrokeIndexAllowed(strokeIndex, allowedStrokeIndices)) {
            return;
          }
          const start = boundary?.start ?? 0;
          const fallbackEnd = sorted[sorted.length - 1].timestamp ?? start;
          const end = boundary?.end ?? fallbackEnd;

          while (pointer < sorted.length && (sorted[pointer].timestamp ?? 0) < start) {
            pointer += 1;
          }

          const strokeSlice: GateForceData[] = [];
          let idx = pointer;
          while (idx < sorted.length && (sorted[idx].timestamp ?? 0) <= end) {
            strokeSlice.push(sorted[idx]);
            idx += 1;
          }

          if (strokeSlice.length >= MIN_STROKE_SAMPLES) {
            const trace = toTraceXYFromPoints(strokeSlice);
            if (trace.length) {
              rowerStrokes.push(decimateTraceSeries(trace));
              const { angle, force } = resampleTraceSeries(trace, TARGET_PROFILE_POINTS);
              if (angle.length && force.length) {
                resampledAngles.push(angle);
                resampledForces.push(force);
              }
            } else {
              skippedCount += 1;
            }
          } else if (strokeSlice.length > 0) {
            skippedCount += 1;
          }

          pointer = idx;
        });
      }

      if (!rowerStrokes.length) {
        const fallbackTraces = detectStrokeCycles(sorted)
          .map((stroke) => decimateTraceSeries(toTraceXYFromPoints(stroke)))
          .filter((stroke) => stroke.length > 0);
        fallbackTraces.forEach((trace) => {
          const { angle, force } = resampleTraceSeries(trace, TARGET_PROFILE_POINTS);
          if (angle.length && force.length) {
            resampledAngles.push(angle);
            resampledForces.push(force);
          }
        });
        strokes[rowerName] = fallbackTraces;
        profiles[rowerName] =
          resampledAngles.length && resampledForces.length
            ? buildMedianProfileFromResampled(resampledAngles, resampledForces)
            : [];
        skipped[rowerName] = skippedCount;
        return;
      }

      strokes[rowerName] = rowerStrokes;
      profiles[rowerName] =
        resampledAngles.length && resampledForces.length
          ? buildMedianProfileFromResampled(resampledAngles, resampledForces)
          : [];
      skipped[rowerName] = skippedCount;
    });

    return { strokes, profiles, skipped };
  }, [gateForceData, corrections.correction, strokeBoundaries, allowedStrokeIndices]);

  return {
    gateForceData,
    loading,
    error,
    corrections,
    correctionDisplay,
    retry,
    rowerNames,
    strokesByRower: processedRowerData.strokes,
    profilesByRower: processedRowerData.profiles,
    skippedStrokesByRower: processedRowerData.skipped,
  };
};
